import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { getTeams, InviteMemberSchema, inviteTeamMember } from "@/queries/team";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as z from "zod";

const inviteMemberSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email format"),
  teamId: z.number({
    required_error: "Please select a team",
  }),
  role: z.enum(["leader", "member"], {
    required_error: "Please select a role",
  }),
});

type InviteMemberFormValues = z.infer<typeof inviteMemberSchema>;

interface InviteMemberFormProps {
  onClose: () => void;
}

export function InviteMemberForm({ onClose }: InviteMemberFormProps) {
  const { mutate: inviteMemberMutation, isPending } = inviteTeamMember();
  const { data: teams = [] } = getTeams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<InviteMemberFormValues>({
    resolver: zodResolver(inviteMemberSchema),
    defaultValues: {
      email: "",
      role: "member",
    },
    mode: "onBlur",
  });

  const onSubmit = async (data: InviteMemberFormValues) => {
    // Clear any previous errors
    setError(null);

    const inviteData: InviteMemberSchema = {
      email: data.email,
      role: data.role,
    };

    inviteMemberMutation(
      { teamId: data.teamId, inviteData },
      {
        onSuccess: () => {
          toast({
            title: "Success",
            description: "Invitation sent successfully.",
          });
          queryClient.invalidateQueries({ queryKey: ["team_members"] });
          onClose();
        },
        onError: (error: any) => {
          // Extract the detailed error message from the API response
          let errorMessage = "Failed to send invitation";

          if (error?.response?.data?.detail) {
            errorMessage = error.response.data.detail;
          } else if (error?.message) {
            errorMessage = error.message;
          }

          setError(errorMessage);
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">Email Address</Label>
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type="email"
              placeholder="Enter user email"
              className="w-full"
            />
          )}
        />
        {errors.email && (
          <p className="text-red-500 text-sm">{errors.email.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="teamId">Team</Label>
        <Controller
          name="teamId"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={(value) => field.onChange(Number(value))}
              value={field.value?.toString()}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a team" />
              </SelectTrigger>
              <SelectContent>
                {teams.map((team) => (
                  <SelectItem key={team.id} value={team.id.toString()}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {errors.teamId && (
          <p className="text-red-500 text-sm">{errors.teamId.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="role">Role</Label>
        <Controller
          name="role"
          control={control}
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
        {errors.role && (
          <p className="text-red-500 text-sm">{errors.role.message}</p>
        )}
      </div>

      <Button type="submit" className="w-full" disabled={isPending}>
        {isPending ? "Sending Invitation..." : "Send Invitation"}
      </Button>

      {error && <div className="mt-4 text-red-500 text-sm">{error}</div>}
    </form>
  );
}
